// Shared plumbing for the homepage hero's animated scene (HeroSky,
// HeroLandscape, HeroBirds, HeroRabbit, HeroGrassFringe).
//
// Home.vue provides a reactive `heroState` object to every scene component:
//
//   heroState.running       false while the hero is scrolled out of view, the
//                           tab is hidden, or the user prefers reduced motion.
//                           JS loops must stop then; CSS animations are paused
//                           by Home.vue through the .is-paused class.
//   heroState.reducedMotion true when prefers-reduced-motion: reduce is set —
//                           render a still, composed frame instead.
//   heroState.width/height  the hero's current size in CSS pixels.
//
// JS-driven components keep their own simulation clock (the sum of the dt
// values they receive), so pausing and resuming never makes anything jump.

// A requestAnimationFrame loop that hands `tick(dt, now)` the elapsed time in
// milliseconds, clamped so a long pause never turns into one giant step.
export function createLoop(tick) {
  let raf = 0;
  let last = 0;
  let running = false;

  const frame = (now) => {
    const dt = last ? Math.min(now - last, 50) : 16;
    last = now;
    tick(dt, now);
    if (running) {
      raf = requestAnimationFrame(frame);
    }
  };

  return {
    start() {
      if (running) return;
      running = true;
      last = 0;
      raf = requestAnimationFrame(frame);
    },
    stop() {
      running = false;
      cancelAnimationFrame(raf);
      raf = 0;
    },
    isRunning() {
      return running;
    }
  };
}

// Small, fast, seedable PRNG so every scene is varied but reproducible.
export function seededRandom(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function lerp(a, b, t) {
  return a + (b - a) * t;
}

export function clamp(v, min, max) {
  return v < min ? min : (v > max ? max : v);
}

export const ease = {
  inOutSine: (t) => -(Math.cos(Math.PI * t) - 1) / 2,
  outCubic: (t) => 1 - Math.pow(1 - t, 3),
  inCubic: (t) => t * t * t,
  inOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  outBack: (t) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  }
};

// Pointer parallax. Home.vue eases --hero-px / --hero-py (-1..1) and writes
// them on the hero section (read by HeroBirds) and on every parallax wrapper
// matched below, which turn them into a translate3d of their own depth.
//
// Both properties are registered as non-inherited: a plain custom property
// inherits into every node of the scene (thousands of SVG elements), so each
// pointer frame restyled the whole forest. Registered, a write only restyles
// the element it is written on. A wrapper therefore has to consume the values
// on itself (not on a descendant) and match this selector; new wrappers
// should simply carry the data-hero-parallax attribute.
export const HERO_PARALLAX_SELECTOR = [
  '[data-hero-parallax]',
  '.sky-far',
  '.sky-clouds',
  '.hero-birds__parallax',
  '.hl-par',
  '.hero-rabbit__stage',
  '.hgf-par'
].join(', ');

let parallaxRegistered = null;

// Registers --hero-px / --hero-py once per document. Returns false where
// CSS.registerProperty is missing: the values then inherit as before, which
// still works, just without the restyle saving.
export function registerHeroParallax() {
  if (parallaxRegistered !== null) return parallaxRegistered;
  parallaxRegistered = false;
  if (typeof window === 'undefined' || !window.CSS || typeof window.CSS.registerProperty !== 'function') {
    return parallaxRegistered;
  }
  ['--hero-px', '--hero-py'].forEach((name) => {
    try {
      window.CSS.registerProperty({ name, syntax: '<number>', inherits: false, initialValue: '0' });
      parallaxRegistered = true;
    } catch (error) {
      // Already registered (a hot reload re-ran this module): same definition.
      if (error && error.name === 'InvalidModificationError') parallaxRegistered = true;
    }
  });
  return parallaxRegistered;
}

// Development-only handle on a scene component (window.__hero.<name>), used
// to pause the clock and render any moment on demand while inspecting frames.
// Background browser tabs throttle requestAnimationFrame, so this is the
// reliable way to look at a specific frame. Never exposed in production.
export function exposeDebug(name, api) {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return;
  window.__hero = window.__hero || {};
  window.__hero[name] = api;
}

export function removeDebug(name) {
  if (typeof window === 'undefined' || !window.__hero) return;
  delete window.__hero[name];
}
